<template>
  <div class="relative">
    <div class="px-20" v-if="meetingInfo">
      <div class="flex justify-between flex-wrap sm:flex-col sm:gap-y-20">
        <div class="flex gap-x-15 sm:flex-col sm:gap-y-20">
          <div v-if="meetingInfo.user">
            <img
              v-if="meetingInfo.user.avatar"
              :src="resizer(meetingInfo.user.avatar)"
              class="organizer-avatar"
              alt="organizer avatar"
              @error="OnImageError"
            />
            <name-avatar v-else rounded="full" :size="hostAvatarSize"
             :text="meetingInfo.user.name" />
          </div>
          <div v-else>
            <img
              v-if="defaultCompany.logo"
              :src="resizer(defaultCompany.logo)"
              class="organizer-avatar"
              alt="organizer avatar"
              @error="OnImageError"
            />
            <name-avatar v-else rounded="full" :size="hostAvatarSize"
             :text="defaultCompany.name" />
          </div>
          <div class="flex flex-col" :class="windowWidth < 640 ? 'flex-gap' : ''">
            <div class="meeting-name-section" v-if="meetingInfo.meeting_name">
              <p class="break-word title-description"
              :title="meetingInfo.meeting_name">
                {{ meetingInfo.meeting_name }}
              </p>
            </div>
            <p v-else class="title-description">{{ $t('busy') }}</p>
            <div v-if="meetingInfo.user" class="meeting-detail">
              <div class="flex flex-col" :class="windowWidth < 640 ? 'flex-gap' : ''">
                <div class="flex sm:flex-col pt-5 gap-x-20"
                 :class="windowWidth < 640 ? 'flex-gap' : ''">
                  <span class="font-bold">{{ meetingInfo.user.name }}</span>
                  <div class="organizer">
                    <div class="organizer-badge">
                      {{ $t('organizer') }}
                    </div>
                    <div
                      class="admin-confirmation-pending-badge"
                      v-if="meetingInfo.status && meetingInfo.status === 'pending'"
                    >
                      {{ $t('awaiting_confirmation') }}
                    </div>
                    <div
                      class="admin-confirmation-reject-badge"
                      v-if="meetingInfo.status && meetingInfo.status === 'reject'"
                    >
                      {{ $t('rejected') }}
                    </div>
                    <div class="confidential-meeting-badge" v-if="meetingInfo.is_private">
                      {{ $t('confidential_meeting') }}
                    </div>
                  </div>
                </div>
                <p class="text-subtitle text-sm font-medium">
                  {{ meetingInfo.user.job_position }}
                </p>
              </div>
            </div>
            <div class="flex flex-wrap">
              <div class="flex gap-x-10 items-center">
                <company-icon /><span class="text-subtitle font-medium text-sm">{{
                  meetingInfo.company_name
                }}</span>
              </div>
              <span class="px-10 text-separator">|</span>
              <div class="flex gap-x-10 items-center">
                <room />
                <span class="text-subtitle font-medium text-sm">
                  {{ meetingInfo.device_name || $t('without_room') }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div class="flex gap-x-20 flex-wrap">
            <div class="time-card">
              <span class="text-subtitle">{{ $t('start') }}</span>
              <div class="flex flex-col items-center">
                <span class="title-timer self-center">
                  {{ meetingInfo.started_at.slice(11, 16) }}</span
                >
                <bdi
                  class="date-context"
                  :class="{ rtl: this.calendar === 'Solar', ltr: this.calendar === 'Gregorian' }"
                >
                  {{ newFormatDateStart }}
                </bdi>
              </div>
            </div>
            <div class="time-card">
              <span class="text-subtitle">{{ $t('end') }}</span>
              <div class="flex flex-col items-center">
                <span class="title-timer self-center">
                  {{ meetingInfo.ended_at.slice(11, 16) }}</span
                >
                <bdi
                  class="date-context"
                  :class="{ rtl: this.calendar === 'Solar', ltr: this.calendar === 'Gregorian' }"
                >
                  {{ newFormatDateEnd }}
                </bdi>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-30 relative" v-if="meetingInfo.meeting_description">
        <div class="description-container" ref="description" disabled>
          <pre class="custom-pre" v-text="meetingInfo.meeting_description"></pre>
          <div
            @click="showFullText"
            v-if="showDescriptionToggleBtn"
            class="description-inner-box"
            :class="{ descriptionGradient: !isExpanded }"
          >
            <button
              :style="{
                transform: isExpanded ? 'rotate(180deg)' : '',
              }"
            >
              <down-arrow />
            </button>
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center pt-30 sm:flex-col">
        <span class="attendee-title">
          {{ $t('attendees') }}
        </span>
        <div class="sm:w-full">
          <base-simple-search-box
            :width="windowWidth < 640 ? 'full' : '210'"
            id="md_s_attendee"
            type="attendee"
            radius="sm"
            :placeholder="$t('search')"
            @searchedText="getSearchedText"
          />
        </div>
      </div>
      <div class="attendee-section">
        <div v-if="filteredAttendee && filteredAttendee.length > 0">
          <div v-if="isLoading && isLoading.name === 'meetingAttendees'" class="attendee-selector">
            <div class="single-attendee" v-for="index in 12" :key="index">
              <div class="single-attendee-section">
                <base-skeleton width="88" height="88" rounded="full" name="meetingAttendees" />
                <div class="flex flex-col items-center">
                  <base-skeleton
                    class="mt-10"
                    width="50"
                    height="10"
                    rounded="sm"
                    name="meetingAttendees"
                  />
                  <base-skeleton
                    class="mt-10"
                    width="50"
                    height="10"
                    rounded="sm"
                    name="meetingAttendees"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="attendee-selector">
            <div class="single-attendee" v-for="attendee in filteredAttendee" :key="attendee.id">
              <div
                class="single-attendee-section"
                :class="attendee.confirm_status === 'await' ? 'opacity-50' : ''"
                @mouseover="
                  confirmTooltip[`${attendee.confirm_status}`] = true;
                  uid = attendee.id;
                "
                @mouseleave="
                  confirmTooltip[`${attendee.confirm_status}`] = false;
                  uid = 0;
                "
              >
                <div class="relative">
                  <img
                    v-if="attendee.avatar && attendee.avatar !== null"
                    :src="resizer(attendee.avatar)"
                    class="attendee-avatar"
                    alt="attendee avatar"
                    @error="OnImageError"
                  />
                  <name-avatar v-else rounded="full" size="88" :text="attendee.name" />
                  <div
                    v-if="attendee.confirm_status === 'yes'"
                    class="accept-confirmation confirmation-status"
                  >
                    <tick-icon />
                    <div class="text-responded">
                      <transition name="fade">
                        <p
                          class="p-4 bg-secondary"
                          v-if="confirmTooltip['yes'] && uid === attendee.id"
                        >
                          {{ $t('responded_on') }}
                          <bdi dir="ltr">{{ confirmedTimeConverter(attendee.responded_on) }}</bdi>
                        </p>
                      </transition>
                    </div>
                  </div>
                  <div
                    v-if="attendee.confirm_status === 'maybe'"
                    class="maybe-confirmation confirmation-status"
                  >
                    <question-mark />
                    <div class="text-responded">
                      <transition name="fade">
                        <p
                          class="p-4 bg-secondary"
                          v-if="confirmTooltip['maybe'] && uid === attendee.id"
                        >
                          {{ $t('responded_on') }}
                          <bdi dir="ltr">{{ confirmedTimeConverter(attendee.responded_on) }}</bdi>
                        </p>
                      </transition>
                    </div>
                  </div>
                  <div
                    v-if="attendee.confirm_status === 'no'"
                    class="deny-confirmation confirmation-status"
                  >
                    <deny-icon :color="'#FF3B30'" />
                    <div class="text-responded">
                      <transition name="fade">
                        <p
                          class="p-4 bg-secondary"
                          v-if="confirmTooltip['no'] && uid === attendee.id"
                        >
                          {{ $t('responded_on') }}
                          <bdi dir="ltr">{{ confirmedTimeConverter(attendee.responded_on) }}</bdi>
                        </p>
                      </transition>
                    </div>
                  </div>
                </div>
                <p
                  class="font-bold pt-5 truncate text-center w-102"
                  dir="auto"
                  :title="attendee.name"
                >
                  {{ attendee.name }}
                </p>
                <div v-if="attendee.is_guest" class="guest-badge">
                  {{ $t('guest') }}
                </div>
                <p class="truncate text-center w-full text-subtitle text-12" dir="auto" v-else>
                  {{ attendee.job_position }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="no-attendee-section" v-else>
          <div class="m-auto">
            {{ $t('no_one_has_been_attended') }}
          </div>
        </div>
      </div>
    </div>
      <admin-confirmation-meeting
      v-if="
        (meetingInfo.status === 'pending' || meetingInfo.status === 'reject') &&
        meetingInfo.is_approve
      "
        :isMeetingRepetitive="meetingInfo.is_repetitive"
        @RefreshMeeting="$emit('getMeetingInfo')"
        ref="adminConfirm"
        :meetingInfo="meetingInfo"
        :currentUserStatus="meetingInfo.status"
      />
      <confirm-meeting
        v-if="meetingInfo.status === 'approve' && !isMeetingOngoingOrPassed &&
         meetingInfo.id && canConfirm"
        :isMeetingRepetitive="meetingInfo.is_repetitive"
        :currentUserStatus="meetingInfo.current_user_status"
        @RefreshMeeting="$emit('getMeetingInfo')"
        ref="confirm"
        :meetingInfo="meetingInfo"
      />
    <edit-recurring-confirmation
      @editConfirmation="sendDataToConfirmation"
      @cancelConfirmation="cancelConfirmationHandler"
      :showThisAndFollowing="false"
      v-if="!open"
    />
    <timeline-conflict-modal
      @reserveAnyway="$refs.confirm.confirmMeetingAnywayHandler()"
      :calendar="calendarType"
      isMeetingConfirmation
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { OnImageError } from '@/utils/on-image-error';
import NameAvatar from '@/components/base/BaseNameAvatar.vue';
import BaseSimpleSearchBox from '@/components/base/BaseSimpleSearchBox.vue';
import BaseSkeleton from '@/components/base/BaseSkeleton.vue';
import TickIcon from '@/assets/icons/vue/Tick.vue';
import QuestionMark from '@/assets/icons/vue/QuestionMark.vue';
import DenyIcon from '@/assets/icons/vue/close_normal.vue';
import ConfirmMeeting from '@/components/ConfirmMeeting.vue';
import EditRecurringConfirmation from '@/views/conference/modals/EditRecurringConfirmation.vue';
import TimelineConflictModal from '@/components/modals/TimelineConflictModal.vue';
import imageResizer from '@/utils/image-resizer';
import DownArrow from '@/assets/icons/vue/downArrow.vue';
import CompanyIcon from '@/assets/icons/vue/CompanyIcon.vue';
import Room from '@/assets/icons/vue/Room.vue';
import AdminConfirmationMeeting from '@/views/conference/AdminConfirmationMeeting.vue';

const moment = require('moment-jalaali');

export default {
  name: 'MeetingDetailInfo',
  components: {
    NameAvatar,
    BaseSimpleSearchBox,
    TickIcon,
    QuestionMark,
    BaseSkeleton,
    DenyIcon,
    ConfirmMeeting,
    EditRecurringConfirmation,
    TimelineConflictModal,
    DownArrow,
    CompanyIcon,
    Room,
    AdminConfirmationMeeting,
  },
  props: {
    meetingInfo: {
      type: Object,
    },
    newFormatDateStart: {
      type: String,
    },
    newFormatDateEnd: {
      type: String,
    },
    calendar: {
      type: String,
    },
  },
  mounted() {
    if (this.$refs.description) {
      this.showDescriptionToggleBtn = this.$refs.description.scrollHeight
      !== this.$refs.description.clientHeight;
      this.$refs.description.style.height = this.showDescriptionToggleBtn ? '100px' : 'fit-content';
    }
    const user = JSON.parse(localStorage.getItem('user'));
    this.calendarType = user ? user.calendar_type : 'Solar';
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  data() {
    return {
      OnImageError,
      moment,
      searchedText: '',
      deviceId: this.$route.params.deviceId || this.meetingInfo.device_id,
      calendarType: '',
      meetingCode: this.$route.params.meetingCode,
      imageSize: 150,
      isExpanded: false,
      showDescriptionToggleBtn: false,
      confirmTooltip: {
        yes: false,
        maybe: false,
        no: false,
      },
      uid: 0,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    canConfirm() {
      if (this.meetingCode && this.$route.name === 'SharedMeeting') {
        return this.meetingInfo.is_guest_confirm;
      }
      return this.isAttendee;
    },
    isMeetingOngoingOrPassed() {
      const START_TIME = new Date(this.meetingInfo.started_at);
      const END_TIME = new Date(this.meetingInfo.ended_at);
      const CURRENT_TIME = new Date();
      if ((START_TIME <= CURRENT_TIME && END_TIME >= CURRENT_TIME) || END_TIME <= CURRENT_TIME) {
        return true;
      }
      return false;
    },
    isAttendee() {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        return this.meetingInfo.attendees.some((attendee) => attendee.id === user.id);
        // eslint-disable-next-line no-else-return
      } else {
        return false;
      }
    },
    ...mapGetters({
      theme: 'getTheme',
      defaultCompany: 'getDefaultCompany',
      currentPermission: 'permissions/getPermissions',
      isLoading: 'loading/getActiveSkeletons',
      open: 'getDrawerState',
    }),
    filteredAttendee() {
      if (this.searchedText && this.searchedText.length > 0) {
        // eslint-disable-next-line arrow-body-style
        return this.meetingInfo.attendees.filter((item) => {
          if (item.name) {
            return item.name.toLowerCase().includes(this.searchedText.toLowerCase());
          }
          return [];
        });
      }
      return this.meetingInfo.attendees;
    },
    hostAvatarSize() {
      if (this.windowWidth > 693 && this.windowWidth < 1024) {
        return '102';
      }
      return '132';
    },
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    cancelConfirmationHandler() {
      if (this.$refs.confirm) {
        this.$refs.confirm.confirmStatus = this.$refs.confirm.previousStatus;
      } else if (this.$refs.adminConfirm) {
        this.$refs.adminConfirm.confirmStatus = this.$refs.adminConfirm.previousStatus;
      }
    },
    sendDataToConfirmation(data) {
      if (this.meetingInfo.status === 'approve') {
        this.$refs.confirm.recurringConfirmationHandler(data);
      } else {
        this.$refs.adminConfirm.recurringAdminConfirmationHandler(data);
      }
    },
    showFullText() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded) {
        this.$refs.description.style.height = `${this.$refs.description.scrollHeight + 15}px`;
      } else {
        this.$refs.description.style.height = this.showDescriptionToggleBtn
          ? '100px'
          : 'fit-content';
      }
    },
    resizer(image) {
      return imageResizer(image, this.imageSize);
    },

    PopOver() {
      this.$emit('PopOver');
    },
    hasPermission(permission) {
      return this.currentPermission.includes(permission);
    },
    getSearchedText(text) {
      this.searchedText = text;
    },
    closeModals(...modals) {
      modals.map((modalName) => this.$store.dispatch('modal/close', modalName));
    },
    confirmedTimeConverter(date) {
      return this.calendar === 'Solar'
        ? moment(date).format('jYYYY-jMM-jDD HH:mm')
        : moment(date).format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>
<style scoped>
.flex-gap {
  gap: 10px;
}
.custom-pre {
  white-space: pre-wrap;
  word-break: break-word;
}
.guest-badge {
  @apply text-info text-sm rounded px-15;
  background-color: rgba(0, 199, 190, 0.1);
}
.title-description {
  @apply text-title font-bold text-4xl lg:text-36 sm:text-28
   truncate max-w-600 md:w-300 sm:w-150 py-20;
}
.organizer {
  @apply flex items-center gap-x-10 flex-wrap;
}
.title-timer {
  @apply text-title font-bold text-36;
}
.attendee-section {
  @apply bg-theme-secondary rounded mt-20;
}
.organizer-avatar {
  @apply rounded-full w-132 sm:w-102 sm:h-102 lg:w-102 lg:h-102 h-132 object-cover;
}
.organizer-badge {
  @apply bg-primaryOpacity text-blueText rounded-sm py-5 text-12 font-bold
   w-82 text-center;
}
.admin-confirmation-pending-badge {
  @apply bg-warning05 rounded-sm py-5 text-12 font-bold px-2 text-center;
  color: #e5b800 !important;
}
.admin-confirmation-reject-badge {
  @apply bg-dangerOpacity rounded-sm py-5 text-12 font-bold px-2 text-center text-danger;
}
.confidential-meeting-badge {
  @apply rounded-sm py-5 text-12 font-bold px-2 text-center;
  color: #722fc6;
  background-color: rgba(115, 47, 198, 0.1);
}
.single-attendee {
  @apply flex;
}
.attendee-avatar {
  @apply w-88 h-88 rounded-full object-cover;
}
.attendee-title {
  @apply text-18 flex justify-start sm:w-full sm:pb-20;
}
.attendee-selector {
  @apply grid grid-cols-7 xl:grid-cols-6
   sm:grid-cols-1 lg:grid-cols-4 md:grid-cols-3 gap-10;
}
.no-attendee-section {
  @apply flex attendee-section;
}
.date-context {
  @apply text-title font-medium text-18;
}
.meeting-detail {
  @apply flex items-baseline;
}
.rtl {
  direction: rtl;
}
.ltr {
  direction: ltr;
}
.time-card {
  @apply shadow-lg rounded w-150 lg:w-120 h-220
   lg:h-170 sm:w-150 sm:h-210 flex flex-col justify-around items-center p-20;
}
.single-attendee-section {
  @apply flex flex-col items-center px-10 border border-default w-170 h-180 rounded-sm py-20;
}
.meeting-name-section {
  @apply max-w-2xl;
}
.confirmation-status {
  @apply flex justify-center items-center absolute h-24 w-24 rounded right-0 bottom-0;
}
.accept-confirmation {
  @apply bg-green-50;
}
.maybe-confirmation {
  @apply bg-tertiary;
}
.deny-confirmation {
  @apply bg-red-50;
}
.description-container {
  @apply overflow-hidden border border-default w-full
   rounded-sm resize-none bg-secondary text-title p-15;
  height: 100px;
  transition: all 0.3s;
}
.descriptionGradient {
  background: linear-gradient(0deg, var(--color-bg-primary) 20%, rgba(255, 255, 255, 0) 100%);
}
.description-inner-box {
  @apply absolute left-0 rounded-sm bottom-0 w-full flex justify-center cursor-pointer h-40;
}
.text-responded {
  @apply absolute shadow-dashboardCardDark z-10 transform -right-128
   top-48 w-max rounded-sm px-10;
}
.text-responded:is(:lang(fa), :lang(ar)) {
  @apply absolute shadow-dashboardCardDark text-center z-10
   transform -right-32 top-48 w-max rounded-sm px-10;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
